import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { AssetResource } from '@models/Core/Asset';
import {
  AudioProject,
  AudioProjectData,
  AudioSegment,
} from '@models/Sessions/Idea/AudioProject';
import { RequestService } from '@services/request.service';
import { MessageService } from 'primeng/api';
import { interval, switchMap } from 'rxjs';

interface GetAudioAssetResourceParams {
  resource: AssetResource;
}

interface GetAudioProjectParams {
  sessionId: string;
  ideaId: string;
}

interface GetAudioProjectDto {
  audioProject: AudioProject;
}

interface PutAudioProjectParams {
  sessionId: string;
  ideaId: string;
}

interface PutAudioProjectDto {
  audioProject: AudioProject;
}

interface PostAudioProjectGenerateParams {
  sessionId: string;
  ideaId: string;
}

interface PostAudioProjectGenerateDto {
  audioProject: AudioProject;
}

interface PostAudioSegmentGenerateParams {
  sessionId: string;
  ideaId: string;
  segmentId: string;
}

interface PostAudioSegmentGenerateDto {
  segment: AudioSegment;
}

interface PatchAudioProjectParams {
  sessionId: string;
  ideaId: string;
}

interface PatchAudioProjectDto {
  audioProject: AudioProject;
}

@Injectable()
export class AudioProjectService {
  constructor(
    private readonly http: HttpClient,
    private readonly client: RequestService,
    private readonly messageService: MessageService,
  ) {}

  polling(params: GetAudioProjectParams) {
    return interval(10000).pipe(switchMap(() => this.get(params)));
  }

  get({ sessionId, ideaId }: GetAudioProjectParams) {
    return this.client.get<GetAudioProjectDto>({
      uri: `/session/${sessionId}/ideas/${ideaId}/projects/audio`,
      handlers: {
        success: () => {
          /** noop */
        },
        error: () => {
          /** noop */
        },
      },
    });
  }

  upsert({ sessionId, ideaId }: PutAudioProjectParams) {
    return this.client.put<undefined, PutAudioProjectDto>({
      uri: `/session/${sessionId}/ideas/${ideaId}/projects/audio`,
      body: undefined,
      handlers: {
        success: () =>
          this.messageService.add({
            severity: 'success',
            summary: 'Audio Project has been created successfully.',
            life: 3000,
          }),
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to create an Audio Project',
            life: 3000,
          }),
      },
    });
  }

  update(
    { sessionId, ideaId }: PatchAudioProjectParams,
    body: AudioProjectData,
  ) {
    return this.client.patch<AudioProjectData, PatchAudioProjectDto>({
      uri: `/session/${sessionId}/ideas/${ideaId}/projects/audio`,
      body,
      handlers: {
        success: () =>
          this.messageService.add({
            severity: 'success',
            summary: 'Audio Project has been updated successfully.',
            life: 3000,
          }),
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to update an Audio Project',
            life: 3000,
          }),
      },
    });
  }

  generateAudio({ sessionId, ideaId }: PostAudioProjectGenerateParams) {
    return this.client.post<void, PostAudioProjectGenerateDto>({
      uri: `/session/${sessionId}/ideas/${ideaId}/projects/audio/generate`,
      body: void 0,
      handlers: {
        success: () =>
          this.messageService.add({
            severity: 'info',
            summary: 'Audio Project asset is being generating...',
            life: 3000,
          }),
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to generate an Audio Project asset',
            life: 3000,
          }),
      },
    });
  }

  generateAudioSegment({
    sessionId,
    ideaId,
    segmentId,
  }: PostAudioSegmentGenerateParams) {
    return this.client.post<void, PostAudioSegmentGenerateDto>({
      uri: `/session/${sessionId}/ideas/${ideaId}/projects/audio/segments/${segmentId}/generate`,
      body: void 0,
      handlers: {
        success: () =>
          this.messageService.add({
            severity: 'info',
            summary: 'Audio Segment asset is being generating...',
            life: 3000,
          }),
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Failed to generate an Audio Segment asset',
            life: 3000,
          }),
      },
    });
  }

  downloadAudio({ resource }: GetAudioAssetResourceParams) {
    return this.http.get([environment.assetsUrl, resource.path].join('/'), {
      responseType: 'blob',
    });
  }
}
